import set from "lodash.set";

import {
  ProgressBarWidgetDto,
  WidgetDeviceType,
  WidgetDto,
  WidgetType,
} from "@intelligems/ig-types";
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { useSelector } from "@src/store/hooks";
import {
  initializeWidget,
  isMessageBox,
  isProgressBar,
  isQuantityButtons,
  setAndValidate,
} from "@src/store/slices/widgets/util";

import {
  IWidgetsState,
  Widget,
  WidgetConfigKey,
  WidgetPropertyKey,
} from "./types";

const initialState: IWidgetsState = {
  isSuccess: false,
  widgets: [],
  selectedWidget: undefined,
  storedShippingWidget: undefined,
};

const widgetsSlice = createSlice({
  name: "widgets",
  initialState,
  reducers: {
    resetWidgets: () => initialState,
    setWidgets: (draft, action: PayloadAction<WidgetDto[]>) => {
      draft.isSuccess = true;
      draft.widgets = action.payload ?? [];
      draft.storedShippingWidget = draft.widgets.find(
        (w) => w.widgetType === "shippingProgressBar" && w.enabledSitewide
      ) as ProgressBarWidgetDto;
    },
    updateWidgetAfterPut: (draft, action: PayloadAction<WidgetDto>) => {
      const index = draft.widgets.findIndex(
        (widget) => widget.id === action.payload.id
      );
      if (index > -1) {
        draft.widgets[index] = action.payload;
      } else {
        draft.widgets.push(action.payload);
      }
      if (action.payload.enabledSitewide) {
        for (const widget of draft.widgets) {
          if (widget.id !== action.payload.id) {
            widget.enabledSitewide = false;
          }
        }
      }
    },
    editWidget: (draft, action: PayloadAction<Widget>) => {
      draft.selectedWidget = {
        mode: "edit",
        value: action.payload,
      };
    },
    createWidget: (draft, action: PayloadAction<WidgetType>) => {
      draft.selectedWidget = {
        mode: "new",
        value: initializeWidget(action.payload),
      };
    },
    closeWidget: (draft, _action: PayloadAction<WidgetDto | undefined>) => {
      draft.selectedWidget = undefined;
    },
    setWidgetConfig: (
      draft,
      action: PayloadAction<{
        device?: WidgetDeviceType;
        key: WidgetConfigKey;
        value: unknown;
      }>
    ) => {
      const widget = draft.selectedWidget;
      if (!widget) {
        return;
      }

      const { device, key, value } = action.payload;
      switch (device) {
        case "desktop":
          setAndValidate(widget.value, "desktop", key, value);
          break;
        case "mobile":
          setAndValidate(widget.value, "mobile", key, value);
          break;
        default:
          setAndValidate(widget.value, "desktop", key, value);
          setAndValidate(widget.value, "mobile", key, value);
          break;
      }
    },
    setBatchedWidgetStyles: (
      draft,
      action: PayloadAction<{
        styles: Record<string, any>;
      }>
    ) => {
      const widget = draft.selectedWidget;
      if (!widget) {
        return;
      }

      const { styles } = action.payload;

      if (!widget.value.config.desktop) {
        widget.value.config.desktop = {};
      }
      if (!widget.value.config.desktop.styles) {
        widget.value.config.desktop.styles = {};
      }

      // Apply all styles to desktop configuration
      Object.entries(styles).forEach(([key, value]) => {
        setAndValidate(widget.value, "desktop", `styles.${key}` as any, value);
      });
    },
  },
  selectors: {
    selectedWidgets: (state) => state.widgets,
    selectedWidget: (state) => state.selectedWidget?.value,
    getMessageBox: (state) => {
      const widget = state.selectedWidget?.value;
      return isMessageBox(widget) ? widget : undefined;
    },
    getProgressBar: (state) => {
      const widget = state.selectedWidget?.value;
      return isProgressBar(widget) ? widget : undefined;
    },
    getQuantityButtons: (state) => {
      const widget = state.selectedWidget?.value;
      return isQuantityButtons(widget) ? widget : undefined;
    },
    hasSelectedWidget: (state) => state.selectedWidget !== undefined,
    getWidgetMode: (state) => state.selectedWidget?.mode,
    getWidgetType: (state) => state.selectedWidget?.value?.widgetType,
    getWidgetEnabledSitewide: (state) =>
      state.selectedWidget?.value?.enabledSitewide,
    selectedStoredShippingWidget: (state) => state.storedShippingWidget,
  },
});

export const widgetsReducer = widgetsSlice.reducer;

export const {
  resetWidgets,
  createWidget,
  editWidget,
  closeWidget,
  setWidgetConfig,
  setBatchedWidgetStyles,
  setWidgets,
  updateWidgetAfterPut,
} = widgetsSlice.actions;

export const {
  selectedWidget,
  selectedWidgets,
  getMessageBox,
  getProgressBar,
  getQuantityButtons,
  getWidgetMode,
} = widgetsSlice.selectors;

export const useWidgetWithStyles = (
  widgetId: string,
  widgetType: WidgetType
) => {
  return useSelector((state) => {
    const widget = selectedWidget(state);
    return widget && widget.id === widgetId && widget.widgetType === widgetType
      ? widget
      : null;
  });
};
